// 레이드 VFX 투사체(화살·빛 구슬·마법 화살·비룡 화염구): 위치만 풀에서 적분하고 모양은 입자 풀(RaidParticles)에 매 프레임 머리·꼬리 입자로 그린다.
// 드로우콜 0(입자 풀을 같이 쓴다). 도착하면 종류별 명중 연출(불똥·광휘·작은 고리, 화염구는 RaidVfx가 큰 폭발을 따로 낸다 — onLand).
import { AddKind, Orient, type RaidParticles } from './raidVfxParticles';
import { GlowKind, type RaidGround } from './raidVfxGround';

export const ProjKind = { Arrow: 0, LightOrb: 1, ArcaneBolt: 2, Fireball: 3 } as const;
export const PROJ_CAP = 24;
/** 꼬리 입자 묶음을 내는 빈도(초당). 렌더 프레임과 무관하게 시간으로 누적하고, 한 프레임에 여러 묶음이면 지나온 길을 따라 나눠 놓는다(Codex P2 #217) */
export const TAIL_HZ = 60;

export class RaidProjectiles {
  private n = 0;
  private readonly sx = new Float32Array(PROJ_CAP); private readonly sy = new Float32Array(PROJ_CAP); private readonly sz = new Float32Array(PROJ_CAP);
  private readonly ex = new Float32Array(PROJ_CAP); private readonly ey = new Float32Array(PROJ_CAP); private readonly ez = new Float32Array(PROJ_CAP);
  private readonly t = new Float32Array(PROJ_CAP); private readonly dur = new Float32Array(PROJ_CAP); private readonly arc = new Float32Array(PROJ_CAP);
  private readonly kind = new Uint8Array(PROJ_CAP); private readonly color = new Uint32Array(PROJ_CAP);
  private readonly lx = new Float32Array(PROJ_CAP); private readonly ly = new Float32Array(PROJ_CAP); private readonly lz = new Float32Array(PROJ_CAP);
  private readonly acc = new Float32Array(PROJ_CAP);
  /** 화염구가 땅에 닿았다(x, z) — 폭발은 소유자가 */
  onLand: ((x: number, z: number) => void) | null = null;
  /** QA: 쏜 수·맞은 수 */
  fired = 0;
  landed = 0;

  constructor(private readonly add: RaidParticles, private readonly glow: RaidGround) {}

  get active(): number { return this.n; }

  /** 쏘기. dur = 날아가는 시간(초), arc = 포물선 꼭대기 높이(m) */
  fire(kind: number, sx: number, sy: number, sz: number, ex: number, ey: number, ez: number, dur: number, arc: number, color: number): void {
    let i = this.n;
    if (i >= PROJ_CAP) { i = 0; for (let k = 1; k < this.n; k++) if (this.t[k] / this.dur[k] > this.t[i] / this.dur[i]) i = k; }
    else this.n++;
    this.sx[i] = sx; this.sy[i] = sy; this.sz[i] = sz; this.ex[i] = ex; this.ey[i] = ey; this.ez[i] = ez;
    this.t[i] = 0; this.dur[i] = Math.max(0.05, dur); this.arc[i] = arc; this.kind[i] = kind; this.color[i] = color;
    this.lx[i] = sx; this.ly[i] = sy; this.lz[i] = sz; this.acc[i] = 1;
    this.fired++;
  }

  private kill(i: number): void {
    const j = --this.n;
    if (i === j) return;
    this.sx[i] = this.sx[j]; this.sy[i] = this.sy[j]; this.sz[i] = this.sz[j]; this.ex[i] = this.ex[j]; this.ey[i] = this.ey[j]; this.ez[i] = this.ez[j];
    this.t[i] = this.t[j]; this.dur[i] = this.dur[j]; this.arc[i] = this.arc[j]; this.kind[i] = this.kind[j]; this.color[i] = this.color[j];
    this.lx[i] = this.lx[j]; this.ly[i] = this.ly[j]; this.lz[i] = this.lz[j]; this.acc[i] = this.acc[j];
  }

  update(dt: number): void {
    if (dt <= 0) return;
    const P = this.add;
    let i = 0;
    while (i < this.n) {
      this.t[i] += dt;
      const p = Math.min(1, this.t[i] / this.dur[i]);
      const x = this.sx[i] + (this.ex[i] - this.sx[i]) * p, z = this.sz[i] + (this.ez[i] - this.sz[i]) * p;
      const y = this.sy[i] + (this.ey[i] - this.sy[i]) * p + this.arc[i] * 4 * p * (1 - p);
      const vx = (x - this.lx[i]) / dt, vy = (y - this.ly[i]) / dt, vz = (z - this.lz[i]) / dt;
      const col = this.color[i], k = this.kind[i];
      // 꼬리 묶음: 시간으로 누적(144Hz도 60Hz와 같은 수), 지나온 선분 위에 고르게 놓는다
      this.acc[i] += dt * TAIL_HZ;
      const steps = Math.min(8, Math.floor(this.acc[i]));
      this.acc[i] -= steps;
      for (let st = 1; st <= steps; st++) {
        const f = st / steps;
        this.puff(k, col, this.lx[i] + (x - this.lx[i]) * f, this.ly[i] + (y - this.ly[i]) * f, this.lz[i] + (z - this.lz[i]) * f, vx, vy, vz);
      }
      this.lx[i] = x; this.ly[i] = y; this.lz[i] = z;
      if (p >= 1) { this.hit(k, x, y, z, vx, vy, vz, col); this.kill(i); continue; }
      i++;
    }
  }

  /** 꼬리 묶음 하나(TAIL_HZ마다) */
  private puff(k: number, col: number, x: number, y: number, z: number, vx: number, vy: number, vz: number): void {
    const P = this.add;
    if (k === ProjKind.Arrow) {
      // 머리: 흰 심 줄(속도 방향 1m 남짓) + 색 꼬리 줄 + 작은 불티
      P.emit(AddKind.Streak, Orient.Velocity, x, y, z, vx, vy, vz, 0.05, 0.09, 0.09, col, 2.2, 1, 0, 0, 0.032);
      P.emit(AddKind.Streak, Orient.Velocity, x, y, z, vx * 0.25, vy * 0.25, vz * 0.25, 0.2, 0.12, 0.04, col, 1.3, 0.55, 0, 3, 0.2);
      if (P.rnd() < 0.6) P.emit(AddKind.Ember, Orient.Camera, x, y, z, P.range(-0.6, 0.6), P.range(0.2, 1), P.range(-0.6, 0.6), 0.35, 0.1, 0.02, col, 1.6, 0.8, -1, 2);
    } else if (k === ProjKind.LightOrb || k === ProjKind.ArcaneBolt) {
      const big = k === ProjKind.ArcaneBolt ? 1.35 : 1;
      P.emit(AddKind.Flare, Orient.Camera, x, y, z, 0, 0, 0, 0.05, 0.55 * big, 0.5 * big, col, 2.0, 1);
      P.emit(AddKind.Ember, Orient.Camera, x, y, z, P.range(-0.8, 0.8), P.range(-0.4, 0.8), P.range(-0.8, 0.8), 0.4, 0.16 * big, 0.03, col, 1.7, 0.85, 0.5, 2);
      if (k === ProjKind.ArcaneBolt) P.emit(AddKind.Streak, Orient.Velocity, x, y, z, vx, vy, vz, 0.07, 0.16, 0.12, col, 1.6, 0.8, 0, 0, 0.04);
    } else {
      // 화염구: 광휘 + 불꽃 덩이 + 불똥(묶음마다 3~4)
      // 화염구 여럿이 입에서 겹쳐 나와도 희게 날아가지 않게 머리는 작고 어둡게, 꼬리는 깊은 주황(2026-10-27 GLB 캡처)
      P.emit(AddKind.Flare, Orient.Camera, x, y, z, 0, 0, 0, 0.05, 0.85, 0.8, 0xff9a40, 1.3, 0.85);
      P.emit(AddKind.Flame, Orient.Camera, x + P.range(-0.2, 0.2), y + P.range(-0.2, 0.2), z + P.range(-0.2, 0.2), -vx * 0.08 + P.range(-0.5, 0.5), -vy * 0.08 + P.range(0, 1.2), -vz * 0.08 + P.range(-0.5, 0.5),
        P.range(0.22, 0.34), P.range(0.7, 1.0), P.range(1.3, 1.8), P.rnd() < 0.6 ? 0xff5a14 : 0xff9a30, 1.5, 0.75, 2.5, 2.5);
      if (P.rnd() < 0.6) P.emit(AddKind.Streak, Orient.Velocity, x, y, z, -vx * 0.2 + P.range(-3, 3), P.range(0, 3), -vz * 0.2 + P.range(-3, 3), 0.4, 0.06, 0.03, 0xffa040, 2.2, 0.9, -6, 1, 0.05);
    }
  }

  private hit(k: number, x: number, y: number, z: number, vx: number, vy: number, vz: number, col: number): void {
    const P = this.add;
    this.landed++;
    if (k === ProjKind.Fireball) { this.onLand?.(x, z); return; }
    const sp = Math.hypot(vx, vy, vz) || 1, dx = vx / sp, dy = vy / sp, dz = vz / sp;
    const sparks = k === ProjKind.Arrow ? 6 : k === ProjKind.ArcaneBolt ? 9 : 5;
    P.emit(AddKind.Flare, Orient.Camera, x, y, z, 0, 0, 0, 0.16, 0.6, 1.4, col, 2.4, 1);
    if (k === ProjKind.ArcaneBolt) P.emit(AddKind.Ring, Orient.Camera, x, y, z, 0, 0, 0, 0.3, 0.6, 2.4, col, 2.0, 0.9);
    // 명중 불똥: 날아온 반대쪽 반구로 튄다
    for (let c = 0; c < P.count(sparks, 2); c++) {
      const a = P.range(0, Math.PI * 2), s = P.range(4, 9);
      const ox = Math.cos(a) * 0.8, oz = Math.sin(a) * 0.8;
      P.emit(AddKind.Streak, Orient.Velocity, x, y, z, (-dx + ox) * s, (-dy + P.range(0.2, 1.2)) * s, (-dz + oz) * s, P.range(0.18, 0.32), 0.07, 0.03, c % 2 ? 0xffffff : col, 2.2, 1, -14, 2, 0.045);
    }
    if (k === ProjKind.LightOrb) this.glow.add(GlowKind.Shock, x, z, 0, 0.3, 1.3, 0.3, 1.3, 0.35, col, 1.3, 0.6);
  }

  clear(): void { this.n = 0; }
}
